import { useRef } from 'react';
import {
	EditOrderPayload,
	GETEventResponseBase,
	GETOrdersResponse,
	GETOrdersResponseEventBASE,
	GETSalesResponse,
	GETSalesResponseEventBASE,
} from '../../types/TixAPITypes';
import { TixBought, TixEvent, TixOrder } from '../../types/TixTypes';
import { useLoadingStore } from '../../store/useLoadingStore';
import { objectWithoutProperties, sleep } from '../scripts';
import { useApiMutation } from '../api/api_v2';
import toast from 'react-hot-toast';
import _ from 'lodash';

export type EditOrderAPIFunc = (data: {
	originalEvent: GETEventResponseBase;
	orderId: TixOrder['id'];
	updatedOrder?: Partial<TixOrder>;
	updatedEvent?: Partial<TixEvent>;
	updatedSeats?: EditOrderPayload['seats'];
	deletedSeats?: TixBought['id'][];
}) => Promise<boolean | undefined>;

export function useEditOrderAPI<T extends GETOrdersResponse | GETSalesResponse>({
	updateState,
}: {
	updateState: React.Dispatch<React.SetStateAction<T | undefined>>;
}) {
	const { fetchData: updateOrderAPI } = useApiMutation('tix.put-order');

	const { removeOrderIdLoading, addOrderIdLoading } = useLoadingStore();

	const currentLoadingIds = useRef<Record<TixOrder['id'], boolean>>({});

	const editOrderAPI: EditOrderAPIFunc = async ({
		originalEvent,
		updatedEvent,
		updatedOrder,
		updatedSeats,
		deletedSeats,
		orderId,
	}: {
		originalEvent: GETOrdersResponseEventBASE | GETSalesResponseEventBASE;
		orderId: TixOrder['id'];
		updatedOrder?: Partial<TixOrder>;
		updatedEvent?: Partial<TixEvent>;
		updatedSeats?: EditOrderPayload['seats'];
		deletedSeats?: TixBought['id'][];
	}) => {
		if (currentLoadingIds.current[orderId]) return;
		addOrderIdLoading(orderId);
		currentLoadingIds.current[orderId] = true;

		// NOTE: Make saving feel more realistic
		await sleep(500);

		try {
			// NOTE: Compile the new order from the original order and changes
			const eventPayload: TixEvent = {
				...objectWithoutProperties<GETOrdersResponseEventBASE | GETSalesResponseEventBASE, TixEvent>(
					originalEvent,
					['orders', 'seats', 'sales', 'stats', 'seatIdsSold', 'seatIdsUnsold'],
				),
				...updatedEvent,
			};
			const orderPayload: TixOrder = {
				...originalEvent.orders[orderId],
				...updatedOrder,
			};
			const seatsPayload: EditOrderPayload['seats'] = Object.values(originalEvent.seats)
				.filter((seat) => seat.orderId === orderId && !deletedSeats?.includes(seat.id))
				.map((seat) => {
					const updatedSeat = updatedSeats?.find((updatedSeat) => updatedSeat.id === seat.id);
					if (!updatedSeat) return seat;
					return { ...seat, ...updatedSeat };
				});
			const updatedSeatIds = seatsPayload.map((v) => v.id).filter((v) => v !== undefined);

			// NOTE: Add created seats the the seats array because above it only updates and removes existing seats
			if (updatedSeats) {
				seatsPayload.push(...updatedSeats.filter((updatedSeat) => updatedSeat.id === undefined));
			}

			// NOTE: Send request and handle error
			const res = await updateOrderAPI({
				body: {
					event: eventPayload,
					order: orderPayload,
					seats: seatsPayload,
				},
			});
			if (!res?.success) {
				console.error(res?.error);
				toast.error(res?.error ?? `Failed to update order ${originalEvent.orders[orderId].reference}}`);
				return;
			}

			// NOTE: On success update the original order
			const updatedRes = res.data;
			updateState((ogState) => {
				if (!ogState) return ogState;
				let state = _.cloneDeep((_.isArray(ogState) ? ogState : ogState.events) as GETEventResponseBase[]);

				// NOTE: If there are deleted seats, delete ALL tixIds which are inside the deleted seats
				if (deletedSeats) {
					for (let i = 0; i < state.length; i++) {
						state[i].sales = state[i].sales.map((sold) => {
							sold.tixId = sold.tixId.filter((tixId) => !deletedSeats.includes(tixId));
							return sold;
						});
					}
				}

				// NOTE: Get the original location of the event
				const originalEventIndex = state.findIndex((event) => event.id === originalEvent.id);

				// NOTE: Event is NOT updated
				if (originalEvent.id === updatedRes.event.id) {
					console.log(`Old order ID: ${state[originalEventIndex].orders[orderId].hash}`);
					state[originalEventIndex].orders[orderId] = updatedRes.order;
					console.log(`New order ID: ${state[originalEventIndex].orders[orderId].hash}`);

					// NOTE: Update all existing seats
					for (const updatedSeatId of updatedSeatIds) {
						const seat = updatedRes.seats.find((updatedSeat) => updatedSeat.id === updatedSeatId);
						if (!seat) continue;
						state[originalEventIndex].seats[updatedSeatId] = seat;
					}

					// NOTE: Delete seats which are deleted
					if (deletedSeats) {
						for (const deletedSeatId of deletedSeats) {
							delete state[originalEventIndex].seats[deletedSeatId];
						}
					}

					// NOTE: Add seats which were added
					for (const newSeat of updatedRes.seats) {
						if (!updatedSeatIds.includes(newSeat.id)) {
							state[originalEventIndex].seats[newSeat.id] = newSeat;
						}
					}
				}
				// NOTE: Event ID changed
				else {
					// NOTE: Filter out the order first
					if (originalEventIndex !== -1) {
						delete state[originalEventIndex].orders[orderId];
						for (const seatId of updatedSeatIds) {
							delete state[originalEventIndex].seats[seatId];
						}
					}

					// NOTE: Delete events with 0 seats
					state = state.filter((event) => Object.keys(event.seats).length > 0);

					// NOTE: Add the new event in a new index or not
					const newEventIndex = state.findIndex((event) => event.id === updatedRes.event.id);
					if (newEventIndex === -1) {
						// NOTE: Filter out old event
						state = [
							...state,
							{
								...updatedRes.event,
								orders: { [updatedRes.order.id]: updatedRes.order },
								seats: Object.fromEntries(updatedRes.seats.map((seat) => [seat.id, seat])),
								sales: updatedRes.sales,
							},
						];
					} else {
						state[newEventIndex].orders[updatedRes.order.id] = updatedRes.order;
						state[newEventIndex].seats = {
							...state[newEventIndex].seats,
							...Object.fromEntries(updatedRes.seats.map((seat) => [seat.id, seat])),
						};
						state[newEventIndex].sales = [...state[newEventIndex].sales, ...updatedRes.sales];
					}
				}

				if (!_.isArray(ogState)) {
					return { ...ogState, events: state } as T;
				} else {
					return state as T;
				}
			});
			return true;
		} catch (err) {
			console.error(err);
			toast.error(err?.toString() ?? 'Undefined error occured when trying to edit order');
			return;
		} finally {
			removeOrderIdLoading(orderId);
			currentLoadingIds.current[orderId] = false;
		}
	};

	return editOrderAPI;
}
